/**
 * `IntegrationsSettings` — GVW-148 integrations health (read-only).
 *
 * Per brief §2 / §10: read-only mirror of platform Admin's integration health.
 * Same shape as GVW-147 but action buttons are replaced with a single
 * "Manage in Admin" pivot per brief §7.
 *
 * Persistence: localStorage snapshot under `gvw-set-integrations` for
 * offline-stale rendering. Live data via `@geosoft/platform-sdk` once the
 * integration-health endpoint lands (TODO(WP-E11)).
 *
 * Cite: design/30-engineering/ui-implementation/GVW-148/brief.md
 */

import { useCallback, useEffect, useState } from 'react';
import { tokens } from '@/design';
import { useLiveRegion } from '@/state/liveRegion';

type IntegrationStatus = 'healthy' | 'degraded' | 'connected-error' | 'expired' | 'permission-denied' | 'not-connected';

interface Integration {
  id: string;
  name: string;
  provider: string;
  status: IntegrationStatus;
  lastHealthCheck: string;
  description: string;
}

const STORAGE_KEY = 'gvw-set-integrations';

const MOCK: Integration[] = [
  { id: 'esri-agol', name: 'ArcGIS Online', provider: 'ESRI', status: 'healthy', lastHealthCheck: new Date(Date.now() - 5 * 60 * 1000).toISOString(), description: 'Cloud-hosted GIS services and data layers' },
  { id: 'postgis', name: 'PostGIS Database', provider: 'PostgreSQL', status: 'degraded', lastHealthCheck: new Date(Date.now() - 30 * 60 * 1000).toISOString(), description: 'Spatial database for survey data' },
  { id: 'oracle-spatial', name: 'Oracle Spatial', provider: 'Oracle', status: 'expired', lastHealthCheck: new Date(Date.now() - 86400 * 1000).toISOString(), description: 'Enterprise spatial data warehouse' },
  { id: 'azure-maps', name: 'Azure Maps', provider: 'Microsoft', status: 'not-connected', lastHealthCheck: new Date(Date.now() - 2 * 86400 * 1000).toISOString(), description: 'Cloud mapping and location services' },
];

const STATUS_GLYPH: Record<IntegrationStatus, { icon: string; label: string; color: string }> = {
  healthy: { icon: 'check_circle', label: 'Connected', color: tokens.color.semantic.success },
  degraded: { icon: 'warning', label: 'Degraded', color: tokens.color.semantic.warning },
  'connected-error': { icon: 'error', label: 'Connection error', color: tokens.color.semantic.danger },
  expired: { icon: 'key', label: 'Expired', color: tokens.color.semantic.danger },
  'permission-denied': { icon: 'block', label: 'Access denied', color: tokens.color.semantic.danger },
  'not-connected': { icon: 'sync_problem', label: 'Not connected', color: tokens.color.ink.muted },
};

function formatRelative(iso: string): string {
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 1440) return `${Math.floor(minutes / 60)}h ago`;
  return `${Math.floor(minutes / 1440)}d ago`;
}

export function IntegrationsSettings() {
  const liveRegion = useLiveRegion();
  const [integrations, setIntegrations] = useState<Integration[]>([]);

  useEffect(() => {
    let next = MOCK;
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored) next = JSON.parse(stored) as Integration[];
      else window.localStorage.setItem(STORAGE_KEY, JSON.stringify(MOCK));
    } catch {
      /* fall back */
    }
    setIntegrations(next);
  }, []);

  useEffect(() => {
    document.title = 'Integrations · GeoViewer';
    liveRegion.announce('status', 'Integrations health loaded.');
  }, [liveRegion]);

  const openInAdmin = useCallback(() => {
    liveRegion.announce('status', 'Opening Platform Admin to manage integrations.');
  }, [liveRegion]);

  return (
    <main
      role="main"
      aria-labelledby="gvw-148-heading"
      data-gvw-id="GVW-148"
      data-route="/workspace/settings/integrations"
      style={{
        minHeight: '100%',
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
        padding: tokens.space[5],
        boxSizing: 'border-box',
      }}
    >
      <h1
        id="gvw-148-heading"
        style={{
          margin: 0,
          marginBottom: tokens.space[4],
          fontSize: tokens.font.size['2xl'],
          fontWeight: tokens.font.weight.semibold,
          display: 'flex',
          alignItems: 'center',
          gap: tokens.space[3],
        }}
      >
        <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 28 }}>
          extension
        </span>
        Integrations
      </h1>

      <div
        role="status"
        style={{
          padding: tokens.space[3],
          marginBottom: tokens.space[4],
          background: tokens.color.surface[1],
          borderLeft: `${tokens.space[1]}px solid ${tokens.color.semantic.info}`,
          borderRadius: tokens.radius.md,
          display: 'flex',
          alignItems: 'center',
          gap: tokens.space[3],
        }}
      >
        <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 18, color: tokens.color.semantic.info }}>
          info
        </span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: tokens.font.weight.medium }}>Read-only mirror</div>
          <div style={{ fontSize: tokens.font.size.sm, color: tokens.color.ink.secondary }}>
            Use Platform Admin to add or modify integrations.
          </div>
        </div>
        <button
          type="button"
          onClick={openInAdmin}
          style={{
            padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
            background: tokens.color.accent.primary,
            color: tokens.color.accent.on,
            border: 'none',
            borderRadius: tokens.radius.md,
            fontSize: tokens.font.size.sm,
            fontWeight: tokens.font.weight.medium,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[1],
          }}
        >
          <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 14 }}>
            open_in_new
          </span>
          Manage
        </button>
      </div>

      <ul
        style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fit, minmax(${tokens.layout.panelMd}px, 1fr))`,
          gap: tokens.space[3],
        }}
      >
        {integrations.map((integration) => {
          const glyph = STATUS_GLYPH[integration.status];
          return (
            <li
              key={integration.id}
              style={{
                padding: tokens.space[4],
                background: tokens.color.surface[1],
                border: tokens.border.thin,
                borderRadius: tokens.radius.md,
                display: 'flex',
                flexDirection: 'column',
                gap: tokens.space[2],
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: tokens.space[2] }}>
                  <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 18, color: glyph.color }}>
                    {glyph.icon}
                  </span>
                  <span style={{ fontSize: tokens.font.size.sm, color: glyph.color, fontWeight: tokens.font.weight.medium }}>
                    {glyph.label}
                  </span>
                </div>
                <span
                  style={{
                    fontSize: tokens.font.size.xs,
                    padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
                    background: tokens.color.surface[3],
                    color: tokens.color.ink.secondary,
                    borderRadius: tokens.radius.chip,
                  }}
                >
                  Read-only
                </span>
              </div>
              <div style={{ fontSize: tokens.font.size.lg, fontWeight: tokens.font.weight.medium }}>{integration.name}</div>
              <div style={{ fontSize: tokens.font.size.sm, color: tokens.color.ink.secondary }}>{integration.provider}</div>
              <div style={{ fontSize: tokens.font.size.sm, color: tokens.color.ink.muted, lineHeight: tokens.font.lineHeight.normal }}>
                {integration.description}
              </div>
              <div style={{ fontSize: tokens.font.size.xs, color: tokens.color.ink.muted }}>
                Last health check {formatRelative(integration.lastHealthCheck)}
              </div>
            </li>
          );
        })}
      </ul>
    </main>
  );
}
